import { db} from '../firebase';
import { 
    collection, 
    query, 
    where, 
    documentId,
    doc, 
    updateDoc, 
    onSnapshot
} from 'firebase/firestore';
import { UserProfile } from '../types';
import { getFunctions, httpsCallable } from "firebase/functions";
import { FirestoreUtils } from './FirestoreUtils';

export const UserService = {
    /**
     * Fetches a list of user profiles by their UIDs.
     * Refactored: Uses chunking to overcome Firestore's 30-item limit.
     */
    async fetchUsersByIds(uids: string[]): Promise<UserProfile[]> {
        if (!uids || uids.length === 0) return [];

        const baseQuery = collection(db, 'users');
        return await FirestoreUtils.getDocsChunked(baseQuery, documentId(), uids);
    },

    /**
     * Subscribes to all users within an organization.
     */
    listenToOrgUsers(orgId: string, callback: (users: UserProfile[]) => void) {
        const q = query(collection(db, 'users'), where('orgId', '==', orgId));
        return onSnapshot(q, (snap) => {
            callback(snap.docs.map(d => ({ id: d.id, ...d.data() } as UserProfile)));
        });
    },

    /**
     * Updates the user's active team selection in Firestore.
     */
    async updateActiveTeams(userId: string, teamIds: string[], activeTeamId: string | null) {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
            activeTeamIds: teamIds,
            activeTeamId: activeTeamId,
            updatedAt: Date.now()
        });
    },

    /**
     * Deletes the user document and optionally the auth account.
     */
    async deleteUserAccount(user: any) {
        // 1. Call the backend to purge all organization data, teams, and tasks atomically
        const deleteFunc = httpsCallable(getFunctions(), 'deleteUserAccountAtomic');
        await deleteFunc();

        // 2. Delete the Firebase Auth record
        await user.delete();
    },

    /**
     * Subscribes to a specific user profile document.
     */
    listenToUserProfile(userId: string, callback: (docSnap: any) => void) {
        const userRef = doc(db, 'users', userId);
        return onSnapshot(userRef, callback);
    },

    /**
     * Performs character-for-character migration of legacy profile fields.
     */
    async migrateUserProfile(userId: string, updates: any) {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, updates);
    }
};
